import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import LoggingAPI from "@/api/logging";
import RelayAPI from "@/api/RelayAPI";
import { Button, Modal, useTheme } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

interface ChangeUserRoleConfirmModalProps {
  show: boolean;
  onClose?: () => void;
  onSubmit?: (defaultInfo?: any) => void;
  onCancel?: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo: any; //
}

export const ChangeUserRoleConfirmModal: React.FC<ChangeUserRoleConfirmModalProps> =
  ({ show, onClose, onSubmit, onCancel, buttonProps, defaultInfo }) => {
    const theme = useTheme();
    const [submitting, setSubmitting] = useState(false);
    const { currentUser } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async () => {
      setSubmitting(true);
      try {
        await RelayAPI.updateUserRole({
          is_admin: defaultInfo.is_admin === 0 ? 1 : 0,
          email: defaultInfo.email,
          tenant_uuid: defaultInfo.tenant_uuid,
        });

        enqueueSnackbar("User role updated", { variant: "success" });

        LoggingAPI.createLog({
          user_id: currentUser.user_id,
          message: `Updated Relay User Role: ${defaultInfo?.email}`,
          event: "relay:update_user_role",
          object_type: "relay",
        });

        onSubmit?.(defaultInfo);
      } catch (error) {
        console.error("Error updating user role:", error);
        enqueueSnackbar(`Error updating user role.`, {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
        onClose?.();
      }
    };

    const handleCancel = () => {
      onCancel?.();
    };

    const title =
      defaultInfo?.is_admin === 0 ? "Grant Admin Role" : "Revoke Admin Role";

    const ConfirmMessage = () => (
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        {defaultInfo?.is_admin === 0 ? (
          <>
            Are you sure you want to revoke admin status from user{" "}
            <span
              style={{ fontWeight: 800, color: theme.palette.primary.main }}
            >
              "{defaultInfo?.full_name}"
            </span>
          </>
        ) : (
          <>
            Are you sure you want to grant admin status to user{" "}
            <span
              style={{ fontWeight: 800, color: theme.palette.primary.main }}
            >
              "{defaultInfo?.full_name}"
            </span>
          </>
        )}
      </div>
    );

    return (
      <Modal open={show} onClose={onClose}>
        <Modal.Title>{title}</Modal.Title>
        <ConfirmMessage />
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleCancel}
            disabled={submitting}
          >
            {buttonProps?.cancelText || "Cancel"}
          </Button>
          <Button
            size="xs"
            color={defaultInfo?.is_admin === 0 ? "green" : "error"}
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            loading={submitting}
          >
            {buttonProps?.submitText || title}
          </Button>
        </ButtonMenu>
      </Modal>
    );
  };
